import SidebarLayout from "components/Layout/Layout";
import { API_URL } from "env";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Pagination, Popconfirm, Skeleton, Tag } from 'antd';
import { EyeIcon, PencilSimpleIcon, PlusIcon, StarIcon, TrashIcon } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const StoreMainPage = () => {
    const [toolData, setToolData] = useState<{
        id: string;
        image: string;
        name: string;
        platform: string;
        star: number;
        review: number;
        price: string;
        discount?: string;
        finalPrice?: string;
    }[]>([]);

    const [page, setPage] = useState<number>(1);
    const [totalData, setTotalData] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    async function fetchToolData(pageNumber: number = 1) {
        setLoading(true);
        try {
            const res = await fetch(API_URL + `api/v1/be/tools?page=${pageNumber}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            const data = await res.json();
            if (data.status) {
                if (data.data.length === 0) {
                    setToolData([]);
                    return;
                }
                setTotalData(data.data.total || 0);
                setPage(pageNumber);
                const mapData = data.data.items.map((item: unknown) => {
                    const i = item as Record<string, unknown>;
                    return {
                        id: i.id as string,
                        image: (i.image_url as string) || '',
                        name: ((i.title as Record<string, string>)?.en) || 'No Name',
                        platform: (i.platform as string) || 'Unknown',
                        star: (i.star as number) || 0,
                        review: (i.review as number) || 0,
                        price: (i.price as string) || '0',
                        discount: (i.price_discount as string) || '0',
                        finalPrice: (i.price_finalize as string) || '0',
                    };
                });
                setToolData(mapData);
            } else {
                toast.error((data.message as string) || 'Failed to fetch data');
            }
        } catch {
            toast.error('An error occurred while fetching data');
        } finally {
            setLoading(false);
        }
    }

    async function DeleteItems(id: string) {
        const res = await fetch(API_URL + `api/v1/be/tools/${id}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
        });
        const data = await res.json();
        if (data.status) {
            toast.success('Item deleted successfully');
            fetchToolData();
        } else {
            toast.error((data.message as string) || 'Failed to delete item');
        }
    }

    useEffect(() => {
        fetchToolData();
    }, []);

    return (
        <SidebarLayout
            title={<h4 className="text-2xl font-bold m-0! py-4">Stores</h4>}
        >
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-text-muted">{totalData} items</p>
                    <Button
                        type="primary"
                        icon={<PlusIcon size={14} />}
                        onClick={() => navigate('/stores/item?type=add')}
                    >
                        Add New Item
                    </Button>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className="bg-bg-card border border-border rounded-sm overflow-hidden shadow-card">
                                <Skeleton.Image active className="w-full!" style={{ width: '100%', height: 180 }} />
                                <div className="p-4">
                                    <Skeleton active paragraph={{ rows: 3 }} />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : toolData.length === 0 ? (
                    <div className="py-20 text-center text-text-muted text-sm bg-bg-card border border-border rounded-xl">
                        No items found
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                        {toolData.map((item) => (
                            <div key={item.id} className="bg-bg-card border border-border rounded-sm overflow-hidden shadow-card hover:shadow-lg transition-shadow duration-200 flex flex-col">
                                {item.image ? (
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-full h-44 object-cover"
                                        onError={e => {
                                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                                            (e.currentTarget.nextElementSibling as HTMLElement).style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div
                                    className="w-full h-44 items-center justify-center bg-bg-secondary"
                                    style={{ display: item.image ? 'none' : 'flex' }}
                                >
                                    <img src="/images/favicon.png" alt="BeQ" className="w-16 h-16 object-contain" />
                                </div>

                                <div className="p-4 flex flex-col gap-2 flex-1">
                                    <div>
                                        <h3 className="text-sm font-semibold text-text-primary line-clamp-2 leading-snug">{item.name}</h3>
                                        <p className="text-xs text-text-muted mt-0.5">ID: {item.id}</p>
                                    </div>

                                    <Tag className="self-start text-xs">{item.platform}</Tag>

                                    <div className="flex items-center gap-1 text-text-secondary">
                                        <StarIcon size={14} weight="fill" className="text-yellow-400" />
                                        <span className="text-xs font-medium">{item.star}</span>
                                        <span className="text-xs text-text-muted">({item.review} reviews)</span>
                                    </div>

                                    <div className="text-sm font-semibold text-text-primary mt-auto">
                                        {item.discount && item.discount !== '0' ? (
                                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                                <span>${item.finalPrice}</span>
                                                <span className="text-xs text-text-muted line-through font-normal">${item.price}</span>
                                                <span className="text-xs text-green-500">({item.discount}% off)</span>
                                            </div>
                                        ) : (
                                            <span>${item.price}</span>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                                        <button
                                            onClick={() => navigate(`/stores/item?type=review&id=${item.id}`)}
                                            className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors cursor-pointer"
                                            title="Preview"
                                        >
                                            <EyeIcon size={13} />
                                            Preview
                                        </button>
                                        <button
                                            onClick={() => navigate(`/stores/item?type=edit&id=${item.id}`)}
                                            className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-sm text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors cursor-pointer"
                                            title="Edit"
                                        >
                                            <PencilSimpleIcon size={13} />
                                            Edit
                                        </button>
                                        <Popconfirm
                                            title="Delete this item?"
                                            description="This action cannot be undone."
                                            onConfirm={() => DeleteItems(item.id)}
                                            okText="Delete"
                                            cancelText="Cancel"
                                            okButtonProps={{ danger: true }}
                                        >
                                            <button
                                                className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors cursor-pointer"
                                                title="Delete"
                                            >
                                                <TrashIcon size={13} />
                                                Delete
                                            </button>
                                        </Popconfirm>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && totalData > 10 && (
                    <div className="flex justify-end">
                        <Pagination
                            current={page}
                            pageSize={10}
                            total={totalData}
                            onChange={(p) => fetchToolData(p)}
                            showSizeChanger={false}
                        />
                    </div>
                )}
            </div>
        </SidebarLayout>
    );
};

export default StoreMainPage;
